/**
 * "Kogemused" profile section (TASK-300): shared types + per-type UI config
 * for the education / work / training repeater entries. One table with a
 * `type` discriminator; the generic detail_1/detail_2 columns get their
 * meaning from these configs. Lives in common so the server (validation)
 * and client (rendering) share one definition.
 */

export const EXPERIENCE_TYPES = ['education', 'work', 'training'] as const
export type ExperienceType = (typeof EXPERIENCE_TYPES)[number]

export const isExperienceType = (value: unknown): value is ExperienceType =>
    typeof value === 'string' && (EXPERIENCE_TYPES as readonly string[]).includes(value)

/** 'YYYY-MM' month stamp used for period_start / period_end. */
export const isValidPeriodMonth = (value: string): boolean => /^\d{4}-(0[1-9]|1[0-2])$/.test(value)

export interface ExperienceEntry {
    id: number
    type: ExperienceType
    name: string
    detail_1: string | null
    detail_2: string | null
    extra_info: string | null
    period_start: string | null
    period_end: string | null
}

/** Create payload — `type` and `name` required, the rest optional. Updates omit `type` (immutable). */
export interface ExperienceInput {
    type: ExperienceType
    name: string
    detail_1?: string | null
    detail_2?: string | null
    extra_info?: string | null
    period_start?: string | null
    period_end?: string | null
}

interface Label {
    et: string
    en: string
}

export interface ExperienceTypeConfig {
    type: ExperienceType
    /** Subsection heading inside the "Kogemused" section. */
    title: Label
    nameLabel: Label
    detail1Label?: Label
    detail2Label?: Label
    /** Only training entries carry the free-form extra info. */
    hasExtraInfo: boolean
    /** Work entries with no period_end render "… praeguseni". */
    ongoingWhenNoEnd: boolean
}

export const EXPERIENCE_TYPE_CONFIGS: ExperienceTypeConfig[] = [
    {
        type: 'education',
        title: { et: 'Kõrg- ja kutseharidus', en: 'Higher & vocational education' },
        nameLabel: { et: 'Haridusasutuse nimi', en: 'Institution' },
        detail1Label: { et: 'Omandatud tase/kraad', en: 'Level/degree' },
        detail2Label: { et: 'Eriala nimetus', en: 'Field of study' },
        hasExtraInfo: false,
        ongoingWhenNoEnd: false,
    },
    {
        type: 'work',
        title: { et: 'Töökogemus', en: 'Work experience' },
        nameLabel: { et: 'Ettevõtte nimi', en: 'Company' },
        detail1Label: { et: 'Ametikoht/kirjeldus', en: 'Position/description' },
        hasExtraInfo: false,
        ongoingWhenNoEnd: true,
    },
    {
        type: 'training',
        title: { et: 'Koolitused, sertifikaadid', en: 'Trainings & certificates' },
        nameLabel: { et: 'Koolituse nimi', en: 'Training' },
        detail1Label: { et: 'Koolitaja', en: 'Trainer' },
        detail2Label: { et: 'Omandatud oskus/tase/sertifikaat', en: 'Skill/level/certificate' },
        hasExtraInfo: true,
        ongoingWhenNoEnd: false,
    },
]

/** '2015-09' → '09.2015' for display. Empty string for null/invalid. */
export const formatPeriodMonth = (value: string | null): string => {
    if (!value || !isValidPeriodMonth(value)) return ''
    const [year, month] = value.split('-')
    return `${month}.${year}`
}

/**
 * Inclusive whole-month span between two 'YYYY-MM' stamps (2020-01 → 2020-01
 * counts as 1). Null when either stamp is missing/invalid or the range is
 * reversed.
 */
export const periodDurationMonths = (start: string | null, end: string | null): number | null => {
    if (!start || !end || !isValidPeriodMonth(start) || !isValidPeriodMonth(end)) return null
    const [startYear, startMonth] = start.split('-').map(Number)
    const [endYear, endMonth] = end.split('-').map(Number)
    const months = (endYear - startYear) * 12 + (endMonth - startMonth) + 1
    return months > 0 ? months : null
}

/** 53 → '4a 5k' (et) / '4y 5m' (en); zero parts omitted. Empty string for null. */
export const formatPeriodDuration = (months: number | null, lang: 'et' | 'en'): string => {
    if (!months) return ''
    const years = Math.floor(months / 12)
    const rest = months % 12
    const [yearSuffix, monthSuffix] = lang === 'et' ? ['a', 'k'] : ['y', 'm']
    const parts: string[] = []
    if (years > 0) parts.push(`${years}${yearSuffix}`)
    if (rest > 0) parts.push(`${rest}${monthSuffix}`)
    return parts.join(' ')
}

/** Newest first: period_start DESC with nulls last, then created_at_utc DESC. */
export const sortExperienceEntries = <
    T extends { period_start: string | null; created_at_utc?: string },
>(
    entries: T[],
): T[] =>
    [...entries].sort((a, b) => {
        if (a.period_start !== b.period_start) {
            if (a.period_start === null) return 1
            if (b.period_start === null) return -1
            return b.period_start.localeCompare(a.period_start)
        }
        return (b.created_at_utc ?? '').localeCompare(a.created_at_utc ?? '')
    })
